import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '../../../services/api';
import { formatDate } from '../../../utils/Utils';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'approved' ? '#2e7d3215' :
    $status === 'rejected' ? '#d32f2f15' :
    $status === 'canceled' ? '#9e9e9e15' :
    '#ed6c0215'};
  color: ${({ $status }) =>
    $status === 'approved' ? '#2e7d32' :
    $status === 'rejected' ? '#d32f2f' :
    $status === 'canceled' ? '#9e9e9e' :
    '#ed6c02'};
`;

export const useMyLeaveApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/leaves/my?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => {
    setRefreshTrigger((p) => p + 1);
  }, []);

  const cancel = useCallback(async (id) => {
    setFetching(true);
    try {
      await api.put(`/v1/leaves/${id}/cancel`);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const statusLabels = useMemo(() => ({
    waiting: t('leave.waiting'),
    approved: t('leave.approved'),
    rejected: t('leave.rejected'),
    canceled: t('leave.canceled'),
  }), [t]);

  const signatureLabels = useMemo(() => ({
    signed: t('leave.signed'),
    unsigned: t('leave.unsigned'),
  }), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'leaveVariationName', label: t('leave.leaveType'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    dateFilterColumn('leaveStart', t('leave.leaveStart'), lang),
    dateFilterColumn('leaveEnd', t('leave.returnDate'), lang),
    { name: 'cost', label: t('leave.cost'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'gte' } } },
    {
      name: 'status', label: t('leave.status'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['waiting', 'approved', 'rejected', 'canceled'],
          renderValue: (v) => statusLabels[v] || v,
        },
        customFilterListOptions: { render: (v) => statusLabels[v] || v },
        customBodyRender: (v) => <StatusBadge $status={v}>{statusLabels[v] || v}</StatusBadge>,
      },
    },
    {
      name: 'signatureStatus', label: t('leave.signatureStatus'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['signed', 'unsigned'],
          renderValue: (v) => signatureLabels[v] || v,
        },
        customFilterListOptions: { render: (v) => signatureLabels[v] || v },
        customBodyRender: (v) => signatureLabels[v] || v,
      },
    },
    { name: 'createDate', label: t('leave.createDate'), options: { filter: false, sort: true, customBodyRender: (v) => formatDate(v, lang) } },
  ], [t, statusLabels, signatureLabels, lang]);

  return { columns, data, fetching, pagination, cancel, refresh };
};
